import React, { useState } from 'react';
import { Upload, Image as ImageIcon, Link, Info } from 'lucide-react';
import { getUploadUrlWithQuota } from '../../services/uploadWithQuota';

interface ImageBlockProps {
  data: any;
  onChange: (data: any) => void;
  onDelete?: () => void;
  isPreview?: boolean;
}

const ImageBlock: React.FC<ImageBlockProps> = ({ data, onChange, isPreview = false }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
      return;
    }

    // Check file size (max 10MB for images)
    if (file.size > 10 * 1024 * 1024) {
      alert('ไฟล์มีขนาดใหญ่เกิน 10MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      console.log('[ImageBlock] Uploading image:', file.name, file.size);
      
      // Upload to Cloudflare R2 via Worker (same as VideoBlock)
      const result = await getUploadUrlWithQuota(
        file.name,
        file.type,
        file.size,
        file,
        (progress) => {
          setUploadProgress(progress);
          console.log(`Upload progress: ${progress}%`);
        }
      );
      
      console.log('[ImageBlock] Upload successful:', result);
      
      onChange({
        url: result.publicUrl,
        fileName: file.name,
        fileSize: file.size,
        isUrl: false
      });
      
      alert('✅ อัปโหลดรูปภาพสำเร็จ!');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      alert(error.message || 'เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUrlSubmit = () => {
    if (!imageUrl.trim()) {
      alert('กรุณาใส่ URL รูปภาพ');
      return;
    }

    // Basic URL validation
    try {
      new URL(imageUrl);
    } catch {
      alert('URL ไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง');
      return;
    }

    onChange({
      url: imageUrl,
      fileName: 'Image from URL',
      isUrl: true
    });
    setImageUrl('');
    setShowUrlInput(false);
  };

  if (!data?.url) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
        {!showUrlInput ? (
          <>
            <label className="flex flex-col items-center cursor-pointer">
              <ImageIcon className="w-16 h-16 text-gray-400 mb-4" />
              <div className="flex items-center gap-2 mb-2">
                <span className="text-gray-600 font-medium">คลิกเพื่ออัปโหลดรูปภาพ</span>
                <div className="relative">
                  <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
                  <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
                    <p className="font-semibold mb-1">เพิ่มรูปภาพ</p>
                    <p className="text-gray-300">อัปโหลดรูปภาพสินค้า แบนเนอร์ หรือโปรโมชั่น เพื่อดึงดูดความสนใจของลูกค้า</p>
                  </div>
                </div>
              </div>
              <span className="text-sm text-gray-500 mb-1">รองรับไฟล์ JPG, PNG, GIF, WebP</span>
              <span className="text-xs text-gray-400">แนะนำขนาด: 1200x630px (อัตราส่วน 1.91:1)</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowUrlInput(true)}
                className="text-blue-500 hover:text-blue-600 text-sm flex items-center gap-2 mx-auto"
              >
                <Link className="w-4 h-4" />
                หรือวางลิงก์รูปภาพ
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col items-center">
              <Link className="w-16 h-16 text-blue-500 mb-4" />
              <span className="text-gray-600 font-medium mb-2">วางลิงก์รูปภาพ</span>
            </div>
            <div className="flex gap-2">
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && handleUrlSubmit()}
              />
              <button
                onClick={handleUrlSubmit}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                เพิ่ม
              </button>
            </div>
            <button
              onClick={() => {
                setShowUrlInput(false);
                setImageUrl('');
              }}
              className="text-gray-500 hover:text-gray-600 text-sm mx-auto block"
            >
              ← กลับไปอัปโหลดไฟล์
            </button>
          </div>
        )}
        {uploading && (
          <div className="mt-4">
            <div className="flex items-center justify-center mb-2">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-200 ease-out"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 text-center">กำลังอัปโหลด... {uploadProgress}%</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative rounded-lg overflow-hidden">
        {/* Upload Progress Overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center z-10">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <div className="w-64 bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-200 ease-out"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 font-semibold">กำลังอัปโหลดรูปภาพ... {uploadProgress}%</p>
            <p className="text-xs text-gray-500 mt-1">กรุณารอสักครู่</p>
          </div>
        )}
        
        <img
          src={data.url}
          alt={data.fileName}
          className="w-full h-auto object-contain max-h-[600px]"
        />
      </div>
      {!isPreview && (
        <>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span className="flex items-center gap-2">
              {data.isUrl && <Link className="w-4 h-4 text-blue-500" />}
              {data.fileName}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUrlInput(true)}
                className="text-blue-500 hover:text-blue-600 flex items-center gap-2"
              >
                <Link className="w-4 h-4" />
                เปลี่ยนเป็น URL
              </button>
              <label className="cursor-pointer text-blue-500 hover:text-blue-600 flex items-center gap-2">
                <Upload className="w-4 h-4" />
                อัปโหลดใหม่
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>
          {showUrlInput && (
            <div className="flex gap-2 mt-2">
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleUrlSubmit()}
              />
              <button
                onClick={handleUrlSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
              >
                เปลี่ยน
              </button>
              <button
                onClick={() => {
                  setShowUrlInput(false);
                  setImageUrl('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                ยกเลิก
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImageBlock;
